/*
 * Audience switching.
 *
 * The palette itself is pure CSS — both are already in the page, scoped by
 * `data-audience` on <html>. All this file does is decide what that attribute
 * should say and remember it for the rest of the browser session.
 *
 * Timing matters as much as the value. When a click is going to navigate we only write
 * the choice to session storage and leave the current page alone: `audience-head` reads
 * it on the destination before the first paint, so the colour turns over together with
 * the new page instead of recolouring the page the visitor is about to leave. The
 * attribute is set here and now only when we are staying put.
 *
 * A logged-in customer is resolved server side and must not be overridden here,
 * so every entry point checks `data-audience-locked` first.
 */
(function () {
  const KEY = 'eminence:audience';
  const VALID = ['mi', 'b2b'];
  const root = document.documentElement;

  const isLocked = () => root.hasAttribute('data-audience-locked');

  function remember(audience) {
    try {
      window.sessionStorage.setItem(KEY, audience);
    } catch (e) {
      /* Private mode, or storage is full — the attribute still applies for this page. */
    }
  }

  function forget() {
    try {
      window.sessionStorage.removeItem(KEY);
    } catch (e) {}
  }

  function applyNow(audience) {
    root.setAttribute('data-audience', audience);
    remember(audience);
    root.dispatchEvent(new CustomEvent('audience:change', { detail: { audience } }));
  }

  function set(audience) {
    if (!VALID.includes(audience) || isLocked()) return false;
    applyNow(audience);
    return true;
  }

  function clear() {
    if (isLocked()) return;
    root.removeAttribute('data-audience');
    forget();
  }

  window.EminenceAudience = {
    get: () => root.getAttribute('data-audience') || null,
    set,
    clear,
  };

  const isPlainClick = (event) =>
    event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

  /*
   * Where a click is headed, if anywhere. Plain links carry `href`; the theme's hover
   * mega-menu instead puts the destination on `<summary data-link>` and navigates from
   * its own click handler (see DetailsDropdown#onSummaryClicked), so both count.
   */
  function destinationOf(target) {
    const anchor = target.closest('a[href], [data-link]');
    if (!anchor) return null;

    const raw = anchor.getAttribute('href') || anchor.getAttribute('data-link') || '';
    if (!raw || raw.charAt(0) === '#' || /^(javascript|mailto|tel|sms):/i.test(raw)) return null;
    if (anchor.target && anchor.target !== '_self') return null;

    try {
      return new URL(raw, window.location.href);
    } catch (e) {
      return null;
    }
  }

  /*
   * Compared by pathname on purpose. The split landing links back to the current page
   * with `?audience=…` so that it still works without JavaScript; treating that as
   * "staying" is what keeps the single-URL home page from navigating on a choice.
   */
  function commit(event, audience, scope) {
    if (isLocked() || !VALID.includes(audience)) return;

    const destination = destinationOf(scope);
    const leaving =
      destination !== null &&
      isPlainClick(event) &&
      destination.pathname !== window.location.pathname;

    if (leaving) {
      remember(audience);
      return;
    }

    if (destination !== null && !isPlainClick(event)) {
      /* Opening in a new tab: seed the choice for it, leave this one as it is. */
      remember(audience);
      return;
    }

    if (destination !== null) {
      event.preventDefault();
    }

    const previouslyFocused = document.activeElement;
    applyNow(audience);
    settleMenu(previouslyFocused);
  }

  /*
   * The header carries both menu arrangements and CSS shows one of them. Swapping without
   * navigating leaves two loose ends, both handled here:
   *
   *   - the <summary> that was just clicked goes into `display: none`, taking the keyboard
   *     focus down to <body> with it, so focus is handed to the same entry in the list that
   *     took its place;
   *   - the pointer stays where it was while the items move under it, and this is a hover
   *     menu, so a drop-down nobody asked for would spring open. Hover is ignored until the
   *     pointer actually moves again, or for 400ms, whichever comes first.
   */
  const HOVER_SETTLE_MS = 400;
  let settleTimer = null;

  function releaseHover() {
    if (settleTimer !== null) {
      window.clearTimeout(settleTimer);
      settleTimer = null;
    }
    root.removeAttribute('data-audience-settling');
    window.removeEventListener('pointermove', releaseHover);
  }

  function settleMenu(previouslyFocused) {
    root.setAttribute('data-audience-settling', '');
    window.removeEventListener('pointermove', releaseHover);
    window.addEventListener('pointermove', releaseHover, { once: true });
    if (settleTimer !== null) window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(releaseHover, HOVER_SETTLE_MS);

    if (!previouslyFocused) return;

    /*
     * The switch attribute is stamped on the whole top-level item and is the same in both
     * arrangements, so it is what identifies "the entry the visitor was on".
     */
    const item = previouslyFocused.closest('[data-audience-switch]');
    if (!item) return;

    const audience = item.getAttribute('data-audience-switch');
    const lists = document.querySelectorAll('[data-audience-menu]');

    for (const list of lists) {
      if (list.offsetParent === null && list.getClientRects().length === 0) continue;

      /*
       * An entry can exist in two forms (drop-down and plain link, one per screen width),
       * so take the one actually on screen.
       */
      const matches = list.querySelectorAll('[data-audience-switch="' + audience + '"]');
      for (const match of matches) {
        if (match.getClientRects().length === 0) continue;

        const target = match.querySelector('summary, a[href], button');
        if (target) {
          target.focus({ preventScroll: true });
          return;
        }
      }
    }
  }

  /* Picking a side on the split landing. */
  document.addEventListener('click', (event) => {
    const choice = event.target.closest('[data-audience-choice]');
    if (!choice) return;

    commit(event, choice.getAttribute('data-audience-choice'), choice);
  });

  /*
   * Menu entries that stand for an audience. `data-audience-switch` is stamped on the
   * whole top-level menu item server-side, so opening the Pro Audio menu or following
   * anything inside it switches the theme.
   *
   * Capture phase, because the theme's menu components call preventDefault on their own
   * triggers before this would otherwise run.
   */
  document.addEventListener(
    'click',
    (event) => {
      const item = event.target.closest('[data-audience-switch]');
      if (!item || event.target.closest('[data-audience-choice]')) return;

      const audience = item.getAttribute('data-audience-switch');

      /*
       * Drawer: the label is a real link sitting inside a <summary>. Left alone, the tap
       * would open the disclosure instead of following the link — that is the whole bug.
       * A <summary> activates on click and only preventDefault stops it, so the navigation
       * is done here rather than left to the browser. The choice is remembered, never
       * applied to the page being left, so the destination paints it on its first frame.
       */
      const follow = event.target.closest('[data-audience-follow]');
      if (follow) {
        if (!isLocked() && VALID.includes(audience)) remember(audience);
        event.stopPropagation();

        if (isPlainClick(event)) {
          event.preventDefault();
          window.location.assign(follow.href);
        }
        return;
      }

      /*
       * Anywhere else on that row is the caret, whose only job is to open the sub-menu.
       * Switching from here would swap the whole list under the finger and take the
       * sub-menu being opened with it — the side changes when the visitor goes to it.
       */
      if (event.target.closest('.drawer__menu-item--navigable')) return;

      commit(event, audience, event.target);
    },
    true
  );

  /*
   * The logo reopens the selector for guests, per the brief. Customers keep a plain
   * link to the home page, since their account already decided the theme for them.
   *
   * The selector only exists on the home page, so clearing the choice elsewhere would
   * strand the visitor with no way to pick again. Off the home page we therefore let the
   * link navigate — it points home, where the landing is waiting.
   */
  document.addEventListener('click', (event) => {
    const logo = event.target.closest('[data-audience-reopen]');
    if (!logo || isLocked() || !isPlainClick(event)) return;

    const destination = destinationOf(logo);
    if (destination && destination.pathname !== window.location.pathname) {
      forget();
      return;
    }

    event.preventDefault();
    clear();
  });
})();
